import config from '@/config'

type HeapKeyValueType = string | number

/**
 * @see https://docs.heap.io/reference
 * */
interface Heap {
  track(event: Exclude<string, 'click' | 'change' | 'submit'>, eventProperties?: Record<HeapKeyValueType, HeapKeyValueType>)

  identify(identity: string)

  resetIdentity()

  addUserProperties(userProperties: Record<Exclude<HeapKeyValueType, 'user_id'>, HeapKeyValueType>)

  addEventProperties(eventProperties: Record<HeapKeyValueType, HeapKeyValueType>)

  removeEventProperty(eventProperty: HeapKeyValueType)

  clearEventProperties()

  load(appId: string, options?: Record<string, unknown>)

  loaded?: true
}

declare global {
  interface Window {
    heap?: Heap
  }
}

let heapLoadStarted = false

const getWindowHeap = (): Heap | undefined => {
  if (typeof window.heap === 'undefined') {
    return
  }

  if (typeof config.HEAP_PROJECT_ID !== 'string') {
    return
  }

  // @see https://github.com/siteminder-au/authx/pull/236
  if (!heapLoadStarted) { // add a lock here to make Heap only load once
    heapLoadStarted = true
    window.heap.load(config.HEAP_PROJECT_ID, {
      disableTextCapture: true, // https://developers.heap.io/docs/web#global-data-redaction-via-disabling-text-capture
    }) // heap only starts loading the JavaScript when this line called
    window.heap.addEventProperties({ product_code: 'authx' })
    return
  }

  if (!window.heap.loaded) {
    return
  }

  return window.heap
}

// do not populate any errors to the caller
const ignoreError = (fn: Function) => {
  try {
    fn()
  } catch (error) {
    console.error('error on heap tracking', error) // eslint-disable-line
  }
}

export const heap = {
  track: (...params: Parameters<Heap['track']>) => {
    ignoreError(() => getWindowHeap()?.track(...params))
  },
  identify: (...params: Parameters<Heap['identify']>) => {
    ignoreError(() => getWindowHeap()?.identify(...params))
  },
  resetIdentity: (...params: Parameters<Heap['resetIdentity']>) => {
    ignoreError(() => getWindowHeap()?.resetIdentity(...params))
  },
  addUserProperties: (...params: Parameters<Heap['addUserProperties']>) => {
    ignoreError(() => getWindowHeap()?.addUserProperties(...params))
  },
  addEventProperties: (...params: Parameters<Heap['addEventProperties']>) => {
    ignoreError(() => getWindowHeap()?.addEventProperties(...params))
  },
  removeEventProperty: (...params: Parameters<Heap['removeEventProperty']>) => {
    ignoreError(() => getWindowHeap()?.removeEventProperty(...params))
  },
  clearEventProperties: (...params: Parameters<Heap['clearEventProperties']>) => {
    ignoreError(() => getWindowHeap()?.clearEventProperties(...params))
  },
}

export enum HeapEvent {
  MfaRegister = 'mfa_register',
  MfaResetViaAuthxLogin = 'mfa_reset_via_authx_login',
  MfaReminderDismiss = 'mfa_reminder_dismiss',
  MfaVerificationRequired = 'mfa_verification_required',
  MfaVerificationSuccess = 'mfa_verification_success',
  MfaVerificationFailed = 'mfa_verification_failed',

  // device id has been renamed to user device token
  // I didn't rename the actual value here to make Heap data backward compatible
  MfaDeviceIdGenerated = 'mfa_device_id_generated',

  TrustedDeviceEnrollStart = 'trusted_device_enroll_start',
  TrustedDeviceEnrollSkip = 'trusted_device_enroll_skip',
  TrustedDevicePasskeyRegisterSuccess = 'trusted_device_passkey_register_success',
  TrustedDeviceResendConfirmationEmail = 'trusted_device_resend_confirmation_email',
  TrustedDeviceMfaPendingSkip = 'trusted_device_mfa_pending_skip',
  TrustedDeviceMfaPendingLeave = 'trusted_device_mfa_pending_leave',
  TrustedDeviceMfaRegisterSuccess = 'trusted_device_mfa_register_success',
  TrustedDeviceDeny = 'trusted_device_deny',
}

// initialise heap
getWindowHeap()
